<template>
  <TableList
    v-rect-loading="loading"
    class="user-management-list"
    row-key="id"
    v-if="showTable"
    :title="$t(`${i18nPrefix}.title.userManagement`)"
    :listData="userList"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :showCreateBtn="false"
    :pagination="pagination"
    :defaultSort="defaultOrder"
    @pagination-change="handlePaginationChange"
    @sort-change="handleSortChange"
  >
    <template #filter>
      <div class="header-left">
        <span class="text">{{ $t(`${i18nPrefix}.label.companyName`) }}</span>
        <span class="company-select">
          <el-select v-model="selectCompany" filterable clearable @change="handleCompanySelect">
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="item.i18nName"
              :style="{ paddingLeft: item.level * 15 + 'px' }"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-search">
        <el-input
          v-model="keywords"
          clearable
          class="search-input"
          :title="$t(`${i18nPrefix}.placeholder.search`)"
          :placeholder="$t(`${i18nPrefix}.placeholder.search`)"
          @input="handleKeywordsChange"
        >
        </el-input>
      </div>
    </template>
    <template #btn-before>
      <div v-if="selectCompany" class="statistics">
        <i class="iconfont iconcheckmark_circle success-icon"></i>
        <el-tooltip effect="dark" placement="bottom">
          <template #content>
            <div>{{ totalStatusTitle }}</div>
          </template>
          <span class="statistics-count">{{ activeCount }} / {{ totalCount }}</span>
        </el-tooltip>
        <i class="iconfont iconerror_Flat_icons error-icon"></i>
        <span class="statistics-count">{{ disableCount }}</span>
      </div>
    </template>
    <template #btn>
      <el-button class="blue-btn" @click="handelAddUser">{{ $t(`${i18nPrefix}.btn.addUser`) }}</el-button>
      <el-button class="blue-btn" v-if="enableExport" @click="handelExportUser">{{ $t(`${i18nPrefix}.btn.exportUser`) }}</el-button>
    </template>
    <template #disabled="{ data, row }">
      <el-switch :value="data" @change="onEnabledChange($event, row)"></el-switch>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import moment from 'moment'

export default {
  components: {
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.company.userManagement',
      loading: false,
      isEn,
      isFirst: true,
      showTable: true,
      userList: [],
      companyList: [],
      wrappedFilterList: [],
      search: {},
      keywords: null,
      rootCompanyId: null,
      selectCompany: null,
      exportLoading: false,
      enableExport: false,
      actionBtn: {
        edit: {
          show: true,
          onclick: this.handleEditUser
        },
        role: {
          show: true,
          onclick: this.handleRole,
          labelI18nKey: 'admin.company.userManagement.btn.role'
        }
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      order: '-status',
      totalCount: 0,
      activeCount: 0,
      disableCount: 0,
      currentCompanyId: null
    }
  },
  computed: {
    defaultOrder () {
      const order = this.order
      if (order.includes('-')) {
        return {
          prop: order.split('-')[1],
          order: 'descending'
        }
      } else {
        return {
          prop: order,
          order: 'ascending'
        }
      }
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.name`),
          dataKey: 'full_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'companyName',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.email`),
          dataKey: 'email_address',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.role`),
          dataKey: 'role_all_titles',
          showOverflowTooltip: true,
          formatter (data) {
            return data.join(',')
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          slotName: 'disabled',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.updateTime`),
          dataKey: 'updated_time',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          showOverflowTooltip: true,
          formatter (data) {
            return moment(data).format('YYYY-MM-DD')
          }
        }
      ]
      return columns
    },
    filterList () {
      const list = [
        {
          filterList: {
            company: {
              elType: 'el-select',
              optionLabel: 'i18nName',
              optionValue: 'id',
              optionSearch: true,
              optionList: this.companyList,
              style: (company) => {
                return { 'padding-left': `${(company.level || 1) * 15}px` }
              },
              class: 'company-select',
              loader: this.loadCompanyList
            }
          }
        }
      ]
      return list
    },
    languageCode () {
      return this.$i18n.locale
    },
    totalStatusTitle () {
      return this.$t(`${this.i18nPrefix}.message.userTip`, { activeCount: this.activeCount, totalCount: this.totalCount })
    }
  },
  created () {
    this.getOrderFromUrl()
    this.initData()
  },
  methods: {
    ...mapActions('userManagement', ['getUserList', 'exportUserList', 'updateUser']),
    ...mapActions('company', ['getCompanyChildrenTree']),
    async initData () {
      this.loading = true
      try {
        await this.loadCompanyList()
        this.getCompanyFromUrl()
        this.getKeywordsFromUrl()
        this.handleRouteChange()
        await this.loadUserList()
      } finally {
        this.loading = false
        this.isFirst = false
      }
    },
    async loadCompanyList () {
      const companyTreeResp = await this.getCompanyChildrenTree()
      const currentCompany = {
        id: companyTreeResp.self.id,
        name: companyTreeResp.self.name,
        cn_name: companyTreeResp.self.cn_name,
        level: companyTreeResp.self.level
      }
      this.selectCompany = currentCompany.id
      this.currentCompanyId = currentCompany.id
      this.rootCompanyId = companyTreeResp.root?.id
      const companyList = []
      this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
    },
    async loadUserList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      try {
        this.loading = true
        const params = {
          company_id: this.selectCompany,
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          order: this.order,
          keywords: this.keywords
        }
        const resp = await this.getUserList(params)
        this.formatAccountCompanyData(resp)
      } finally {
        this.loading = false
      }
    },
    formatAccountCompanyData (resp) {
      if (resp) {
        _.forEach(resp.account_list, account => {
          addI18nProperty(account, 'companyName', 'company.cn_name', 'company.name')
        })
        this.userList = resp.account_list
        this.pagination.total = resp.total_count
        this.totalCount = resp.total_num
        this.activeCount = resp.active_num
        this.disableCount = resp.disable_num
        this.enableExport = resp.enable_export
        this.isExpiredPremium = resp.is_expired_premium
      }
    },
    handlePaginationChange () {
      this.loadUserList()
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.handleRouteChange(true)
    },
    handleCompanySelect () {
      this.handleRouteChange(true)
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleKeywords, 1000)
      }
      this.keywordsSearchDebounce()
    },
    handleKeywords () {
      this.handleRouteChange(true)
    },
    async onEnabledChange (status, row) {
      this.loading = true
      try {
        const data = {
          account_id: row.id,
          status: status,
          account: {
            company_id: row.company.id
          }
        }
        const resp = await this.updateUser(data)
        if (resp && resp.id) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          row.status = resp.status === '1'
          if (row.status) {
            this.activeCount++
            this.disableCount--
          } else {
            this.activeCount--
            this.disableCount++
          }
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
        }
      } finally {
        this.loading = false
      }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        _.forEach(children, (child) => {
          this.formatCompanyData(data, child, companyList)
        })
      }
      return companyList
    },
    handelAddUser () {
      if (!this.isExpiredPremium || this.activeCount >= this.totalCount) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.expireDateClosed`))
        return
      }
      this.$router.push({ path: '/v-admin/user/management/add', query: { company: this.selectCompany || this.currentCompanyId } })
    },
    handelExportUser () {
      window.location.href = `/django_rest/user/list/export/?company_id=${this.selectCompany}`
    },
    handleEditUser (row) {
      this.$router.push({ path: `/v-admin/user/management/edit/${row.id}`, query: { company: this.selectCompany, keywords: this.keywords } })
    },
    handleRole (row) {
      this.$router.push({ path: `/v-admin/user/management/role/${row.id}`, query: { company: this.selectCompany, keywords: this.keywords } })
    },
    getOrderFromUrl () {
      const order = this.$route.query.order
      if (order) {
        this.order = order
      } else {
        this.order = '-status'
      }
    },
    getCompanyFromUrl () {
      const selectCompany = parseInt(this.$route.query.company)
      if (selectCompany) {
        this.selectCompany = selectCompany
      }
    },
    getKeywordsFromUrl () {
      const keywords = this.$route.query.keywords
      if (keywords) {
        this.keywords = keywords
      }
    },
    handleRouteChange (isPush) {
      const selectCompany = this.$route.query.company
      const order = this.$route.query.order
      const keywords = this.$route.query.keywords
      if (((selectCompany || this.selectCompany) && selectCompany !== `${this.selectCompany}`) || (this.order && order !== this.order) ||
      ((keywords || this.keywords) && keywords !== this.keywords)) {
        isPush
          ? this.$router.push({ query: { company: this.selectCompany, order: this.order, keywords: this.keywords } })
          : this.$router.replace({ query: { company: this.selectCompany, order: this.order, keywords: this.keywords } })
      }
    }
  },
  watch: {
    $route () {
      this.showTable = false
      this.$nextTick(() => {
        this.showTable = true
        this.getOrderFromUrl()
        this.getCompanyFromUrl()
        this.getKeywordsFromUrl()
        this.handleRouteChange()
        if (!this.isFirst) {
          this.loadUserList(true)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.user-management-list {

  .statistics {
    display: inline-block;

    .success-icon {
      font-size: 20px;
      color: #0096cc;
      vertical-align: middle;
      margin-right: 5px;
    }

    .error-icon {
      font-size: 17px;
      color: #aaa;
      vertical-align: middle;
      margin-right: 5px;
    }

    .statistics-count {
      border-right: 1px solid #aaa;
      margin-right: 10px;
      padding-right: 10px;
      vertical-align: middle;
    }
  }
  .header-left {
    display: inline-block;
    vertical-align: middle;
    .text {
      margin-right: 10px;
      display: inline-block;
      vertical-align: middle;
    }

    .company-select {
      display: inline-block;
      vertical-align: middle;
    }
  }

}
</style>
